/**
 * Onboarding Flow — Guided Setup Experience
 *
 * 5 steps: Welcome → Rhythm → Goal → Income → Complete
 * Then plan preview overlay → flow entry → completion.
 *
 * Calm, clear, one action per step. No overwhelm.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { useRhythm } from "../components/rhythm/RhythmProvider";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowLeft,
  Loader2,
  Calendar,
  Target,
  Wallet,
  Check,
  Sparkles,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============== Step 1: Welcome ==============

function WelcomeStep({ onNext }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] text-center" data-testid="onboarding-step-welcome">
      <div className="w-20 h-20 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center mb-8">
        <Sparkles className="w-10 h-10 text-[#1a4d3e]" />
      </div>
      <h1 className="font-heading text-3xl font-bold text-foreground mb-3">
        Let's set this up together
      </h1>
      <p className="text-muted-foreground max-w-sm mb-2">
        This won't take long.
      </p>
      <p className="text-muted-foreground max-w-sm mb-10">
        We'll go one step at a time so everything feels clear and easy.
      </p>
      <Button
        size="lg"
        onClick={onNext}
        className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90 px-10"
        data-testid="onboarding-start-btn"
      >
        Start
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
}

// ============== Step 2: Rhythm ==============

const RHYTHM_OPTIONS = [
  { value: "weekly", label: "Weekly", desc: "Every 7 days" },
  { value: "fortnightly", label: "Fortnightly", desc: "Every 14 days" },
  { value: "monthly", label: "Monthly", desc: "1st to end of month" },
  { value: "per_pay_cycle", label: "Per pay cycle", desc: "Based on when you get paid" },
];

const DAY_OPTIONS = [
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
];

function RhythmStep({ onNext, onBack }) {
  const [selected, setSelected] = useState("weekly");
  const [anchorType, setAnchorType] = useState("date");
  const [anchorValue, setAnchorValue] = useState("1");
  const [saving, setSaving] = useState(false);

  const handleContinue = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/onboarding/rhythm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          rhythm_type: selected,
          pay_anchor_type: selected === "per_pay_cycle" ? anchorType : null,
          pay_anchor_value: selected === "per_pay_cycle" ? anchorValue : null,
        }),
      });
      onNext();
    } catch {
      toast.error("Could not save rhythm");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="onboarding-step-rhythm">
      <div className="text-center space-y-2">
        <div className="w-14 h-14 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center mx-auto mb-4">
          <Calendar className="w-7 h-7 text-[#1a4d3e]" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-foreground">
          How does your money usually flow?
        </h2>
        <p className="text-muted-foreground max-w-sm mx-auto">
          Choose a rhythm that matches your household — you can change this anytime.
        </p>
      </div>

      <div className="space-y-2">
        {RHYTHM_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setSelected(opt.value)}
            className={`w-full flex items-center justify-between p-4 rounded-xl border text-left transition-colors ${
              selected === opt.value
                ? "border-[#1a4d3e] bg-[#1a4d3e]/5"
                : "border-border hover:border-muted-foreground/30"
            }`}
            data-testid={`onboarding-rhythm-${opt.value}`}
          >
            <div>
              <p className="text-sm font-medium">{opt.label}</p>
              <p className="text-xs text-muted-foreground">{opt.desc}</p>
            </div>
            {selected === opt.value && <Check className="w-4 h-4 text-[#1a4d3e]" />}
          </button>
        ))}
      </div>

      {selected === "per_pay_cycle" && (
        <div className="border-t border-border pt-4 space-y-3">
          <p className="text-sm font-medium">When does your income usually arrive?</p>
          <div className="flex gap-2">
            <button
              onClick={() => setAnchorType("date")}
              className={`flex-1 p-2 rounded-lg border text-sm transition-colors ${
                anchorType === "date" ? "border-[#1a4d3e] bg-[#1a4d3e]/5" : "border-border"
              }`}
              data-testid="onboarding-anchor-date"
            >
              Specific date
            </button>
            <button
              onClick={() => setAnchorType("day")}
              className={`flex-1 p-2 rounded-lg border text-sm transition-colors ${
                anchorType === "day" ? "border-[#1a4d3e] bg-[#1a4d3e]/5" : "border-border"
              }`}
              data-testid="onboarding-anchor-day"
            >
              Day of week
            </button>
          </div>
          {anchorType === "date" ? (
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: 28 }, (_, i) => {
                const v = String(i + 1);
                const suffix = ["st","nd","rd"][((i+1)%100-20)%10-1] || ["st","nd","rd"][(i+1)%100-1] || "th";
                return (
                  <button
                    key={v}
                    onClick={() => setAnchorValue(v)}
                    className={`p-1.5 rounded text-xs font-medium transition-colors ${
                      anchorValue === v ? "bg-[#1a4d3e] text-white" : "hover:bg-muted"
                    }`}
                    data-testid={`onboarding-date-${v}`}
                  >
                    {i+1}{suffix}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-1">
              {DAY_OPTIONS.map((d) => (
                <button
                  key={d.value}
                  onClick={() => setAnchorValue(d.value)}
                  className={`w-full p-2 rounded-lg border text-left text-sm transition-colors ${
                    anchorValue === d.value ? "border-[#1a4d3e] bg-[#1a4d3e]/5" : "border-border"
                  }`}
                  data-testid={`onboarding-day-${d.value}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between pt-4">
        <Button variant="ghost" onClick={onBack} data-testid="onboarding-rhythm-back">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={handleContinue}
          disabled={saving}
          className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90"
          data-testid="onboarding-rhythm-continue"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue"}
          {!saving && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>
      </div>
    </div>
  );
}

// ============== Step 3: Goal ==============

function GoalStep({ onNext, onBack }) {
  const [goalName, setGoalName] = useState("");
  const [goalAmount, setGoalAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!goalName.trim()) {
      toast.error("Enter a goal name");
      return;
    }
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/onboarding/goal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          goal_name: goalName.trim(),
          goal_amount: goalAmount ? parseFloat(goalAmount) : null,
        }),
      });
      onNext();
    } catch {
      toast.error("Could not save goal");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="onboarding-step-goal">
      <div className="text-center space-y-2">
        <div className="w-14 h-14 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center mx-auto mb-4">
          <Target className="w-7 h-7 text-[#1a4d3e]" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-foreground">
          Start with one goal
        </h2>
        <p className="text-muted-foreground max-w-sm mx-auto">
          What's something you want to move toward right now?
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="goalName">Goal name</Label>
          <Input
            id="goalName"
            placeholder="e.g., Build an emergency fund"
            value={goalName}
            onChange={(e) => setGoalName(e.target.value)}
            className="h-12"
            data-testid="onboarding-goal-name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="goalAmount">
            Target amount <span className="text-muted-foreground text-xs">(optional)</span>
          </Label>
          <Input
            id="goalAmount"
            type="number"
            placeholder="e.g., 5000"
            value={goalAmount}
            onChange={(e) => setGoalAmount(e.target.value)}
            className="h-12"
            data-testid="onboarding-goal-amount"
          />
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="ghost" onClick={onBack} data-testid="onboarding-goal-back">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || !goalName.trim()}
          className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90"
          data-testid="onboarding-goal-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save goal"}
          {!saving && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>
      </div>
    </div>
  );
}

// ============== Step 4: Income ==============

function IncomeStep({ onNext, onBack }) {
  const [source, setSource] = useState("");
  const [amount, setAmount] = useState("");
  const [frequency, setFrequency] = useState("monthly");
  const [saving, setSaving] = useState(false);

  const FREQ_OPTIONS = [
    { value: "weekly", label: "Weekly" },
    { value: "fortnightly", label: "Fortnightly" },
    { value: "monthly", label: "Monthly" },
  ];

  const handleSave = async () => {
    if (!source.trim() || !amount) {
      toast.error("Enter source and amount");
      return;
    }
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/onboarding/income`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          source: source.trim(),
          amount: parseFloat(amount),
          frequency,
        }),
      });
      onNext();
    } catch {
      toast.error("Could not save income");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="onboarding-step-income">
      <div className="text-center space-y-2">
        <div className="w-14 h-14 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center mx-auto mb-4">
          <Wallet className="w-7 h-7 text-[#1a4d3e]" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-foreground">
          What's coming in?
        </h2>
        <p className="text-muted-foreground max-w-sm mx-auto">
          Add your income so you can decide where it goes — before it arrives.
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="incomeSource">Source</Label>
          <Input
            id="incomeSource"
            placeholder="e.g., Salary, Freelance"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="h-12"
            data-testid="onboarding-income-source"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="incomeAmount">Amount</Label>
          <Input
            id="incomeAmount"
            type="number"
            placeholder="e.g., 4500"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="h-12"
            data-testid="onboarding-income-amount"
          />
        </div>
        <div className="space-y-2">
          <Label>Frequency</Label>
          <div className="flex gap-2">
            {FREQ_OPTIONS.map((f) => (
              <button
                key={f.value}
                onClick={() => setFrequency(f.value)}
                className={`flex-1 p-2.5 rounded-lg border text-sm font-medium transition-colors ${
                  frequency === f.value
                    ? "border-[#1a4d3e] bg-[#1a4d3e]/5 text-[#1a4d3e]"
                    : "border-border hover:border-muted-foreground/30"
                }`}
                data-testid={`onboarding-freq-${f.value}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="ghost" onClick={onBack} data-testid="onboarding-income-back">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || !source.trim() || !amount}
          className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90"
          data-testid="onboarding-income-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add income"}
          {!saving && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>
      </div>
    </div>
  );
}

// ============== Step 5: Plan Preview + Completion ==============

function PlanPreviewStep({ onContinue }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] text-center" data-testid="onboarding-step-plan-preview">
      <div className="w-20 h-20 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center mb-6">
        <Sparkles className="w-10 h-10 text-[#1a4d3e]" />
      </div>
      <h2 className="font-heading text-2xl font-bold text-foreground mb-3">
        Here's your starting point
      </h2>
      <p className="text-muted-foreground max-w-sm mb-3">
        Your plan will build from here — you can adjust it anytime.
      </p>
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        <span className="text-xs bg-[#1a4d3e]/10 text-[#1a4d3e] px-3 py-1.5 rounded-full font-medium">Planned Expenses</span>
        <span className="text-xs bg-[#1a4d3e]/10 text-[#1a4d3e] px-3 py-1.5 rounded-full font-medium">Needs</span>
        <span className="text-xs bg-[#1a4d3e]/10 text-[#1a4d3e] px-3 py-1.5 rounded-full font-medium">Future</span>
        <span className="text-xs bg-[#1a4d3e]/10 text-[#1a4d3e] px-3 py-1.5 rounded-full font-medium">Wants</span>
        <span className="text-xs bg-[#1a4d3e]/10 text-[#1a4d3e] px-3 py-1.5 rounded-full font-medium">Unexpected</span>
      </div>
      <Button
        size="lg"
        onClick={onContinue}
        className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90 px-10"
        data-testid="onboarding-plan-continue"
      >
        Continue
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
}

function FlowEntryStep({ onFinish }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] text-center" data-testid="onboarding-step-flow-entry">
      <div className="w-20 h-20 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center mb-6">
        <Check className="w-10 h-10 text-[#1a4d3e]" />
      </div>
      <h2 className="font-heading text-2xl font-bold text-foreground mb-3">
        Keep things clear
      </h2>
      <p className="text-muted-foreground max-w-sm mb-8">
        Just check anything that needs a category — this usually takes a minute.
      </p>
      <Button
        size="lg"
        onClick={onFinish}
        className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90 px-10"
        data-testid="onboarding-go-to-flow"
      >
        Go to transactions
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
}

function CompletionStep({ onDone }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] text-center" data-testid="onboarding-step-complete">
      <div className="w-20 h-20 rounded-full bg-[#1a4d3e]/10 flex items-center justify-center mb-6">
        <Check className="w-10 h-10 text-[#1a4d3e]" />
      </div>
      <h2 className="font-heading text-2xl font-bold text-foreground mb-3">
        You're set
      </h2>
      <p className="text-muted-foreground max-w-sm mb-2">
        You now have a clear starting point.
      </p>
      <p className="text-muted-foreground max-w-sm mb-8">
        From here, it's just small steps each cycle.
      </p>
      <Button
        size="lg"
        onClick={onDone}
        className="bg-[#1a4d3e] hover:bg-[#1a4d3e]/90 px-10"
        data-testid="onboarding-done-btn"
      >
        Go to your overview
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
}

// ============== Main Component ==============

const STEPS = ["welcome", "rhythm", "goal", "income"];

export default function OnboardingFlowPage() {
  const { checkAuth } = useAuth();
  const { updateRhythm } = useRhythm();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [backendState, setBackendState] = useState("welcome");
  // Local phase tracks the frontend overlays after backend completion
  const [phase, setPhase] = useState("backend"); // backend | plan_preview | flow_entry | completion

  const fetchState = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/onboarding/state`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        if (data.current_state === "completed") {
          navigate("/dashboard");
          return;
        }
        setBackendState(data.current_state);
      }
    } catch {
      toast.error("Could not load onboarding state");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchState(); }, [fetchState]);

  const advanceBackend = async () => {
    try {
      const res = await fetch(`${API_URL}/api/onboarding/next`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        if (data.current_state === "completed") {
          // Backend done — show plan preview overlay
          setPhase("plan_preview");
        } else {
          setBackendState(data.current_state);
        }
      }
    } catch {
      toast.error("Could not advance");
    }
  };

  const goBackBackend = async () => {
    try {
      const res = await fetch(`${API_URL}/api/onboarding/back`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setBackendState(data.current_state);
      }
    } catch {
      toast.error("Could not go back");
    }
  };

  const handleComplete = async () => {
    await checkAuth();
    navigate("/dashboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a4d3e]" />
      </div>
    );
  }

  // Calculate progress
  const stepIndex = phase === "backend" ? STEPS.indexOf(backendState) : STEPS.length;
  const progressPercent = Math.min(((stepIndex + 1) / (STEPS.length + 1)) * 100, 100);
  const stepLabel = phase === "backend" ? `Step ${stepIndex + 1} of ${STEPS.length}` : "Almost there";

  return (
    <div className="min-h-screen bg-background" data-testid="onboarding-flow">
      <div className="container max-w-lg mx-auto px-4 py-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between text-xs text-muted-foreground mb-2">
            <span>{stepLabel}</span>
          </div>
          <Progress value={progressPercent} className="h-1.5" data-testid="onboarding-progress" />
        </div>

        {/* Step Content */}
        <div className="bg-card rounded-2xl border border-border p-6 md:p-8 shadow-sm">
          {phase === "backend" && backendState === "welcome" && (
            <WelcomeStep onNext={advanceBackend} />
          )}

          {phase === "backend" && backendState === "rhythm" && (
            <RhythmStep onNext={advanceBackend} onBack={goBackBackend} />
          )}

          {phase === "backend" && backendState === "goal" && (
            <GoalStep onNext={advanceBackend} onBack={goBackBackend} />
          )}

          {phase === "backend" && backendState === "income" && (
            <IncomeStep onNext={advanceBackend} onBack={goBackBackend} />
          )}

          {phase === "plan_preview" && (
            <PlanPreviewStep onContinue={() => setPhase("flow_entry")} />
          )}

          {phase === "flow_entry" && (
            <FlowEntryStep onFinish={() => setPhase("completion")} />
          )}

          {phase === "completion" && (
            <CompletionStep onDone={handleComplete} />
          )}
        </div>

        {/* Helper text */}
        <p className="text-center text-xs text-muted-foreground/60 mt-6">
          You can always adjust these later in Settings.
        </p>
      </div>
    </div>
  );
}
